/**
 * FlashBook — the audio session a recording borrows.
 *
 * Recording is the one thing in this app that changes what the device's audio
 * hardware *is*. On iOS, capturing from the microphone means the shared session
 * becomes a `.playAndRecord` category, and playback of anything afterwards —
 * a take the creator just recorded, the reading on the next page, the app's own
 * sound effects — is routed by that category: through the earpiece rather than
 * the speaker, at the low volume a phone call is mixed for. A creator heard their
 * own recording come back almost silent and reported it as the play button being
 * broken, which from the outside is exactly what it is.
 *
 * Nothing in this app ever told the platform what session it wanted, so the one
 * the recorder set up stayed in place for the rest of the visit. This module is
 * that missing half: **ask before recording, give it back afterwards**.
 *
 * Two things it deliberately is not:
 *
 *  - *not a browser concern.* `expo-audio`'s web implementation of
 *    `setAudioModeAsync` is an empty function, and a recording in a browser opens
 *    no session at all, so the whole module is a no-op there — the calls are kept
 *    unconditional at the call sites rather than guarded with `Platform.OS`,
 *    because the guard belongs to the platform and not to every caller.
 *  - *not fatal.* Every call is best-effort: a platform that refuses an audio
 *    mode must not stop a creator from recording a reading, and there is nothing
 *    useful to tell them about it — the recording either works or the recorder
 *    says so itself.
 */
import {Platform} from 'react-native'

/**
 * Whether the session has been asked for recording mode.
 *
 * Tracked here so the restore below can be a no-op when there is nothing to
 * restore: `setAudioModeAsync` is a native call, and calling it after every
 * screen that happened to touch this module would be work for nobody.
 */
let borrowed = false

/** What the app plays with. The mode a device should be in when nothing is being recorded. */
const PLAYBACK: {
  allowsRecording: boolean
  playsInSilentMode: boolean
  interruptionMode: 'mixWithOthers' | 'duckOthers' | 'doNotMix'
} = {
  allowsRecording: false,
  /*
   * A reading is content, not a notification: on iOS the silent switch must not
   * silence a book. This is also the flag that decides the *category* — with it
   * off, the session becomes `.ambient` and a muted phone mutes the book, which
   * is the wrong answer for material a learner is studying.
   */
  playsInSilentMode: true,
  // A book plays alongside whatever else the learner has open unless it is
  // literally a recording session; `doNotMix` is what keeps the microphone
  // reliable while recording and is asked for only in `beginRecordingSession`.
  interruptionMode: 'mixWithOthers',
}

/**
 * Take the session for recording.
 *
 * `allowsRecording: true` is not a hint: on iOS it is the flag that turns the
 * category into `.playAndRecord` *and* the flag the recorder's own `record()`
 * checks before it will capture anything. Without it, pressing Record on an
 * iPhone threw, and the field had nothing to show for it but "Recording is not
 * available on this device".
 */
export async function beginRecordingSession(): Promise<void> {
  if (Platform.OS === 'web') return
  try {
    const {setAudioModeAsync} = await import('expo-audio')
    await setAudioModeAsync({
      allowsRecording: true,
      playsInSilentMode: true,
      // The microphone wants the room to itself while it is open: ducking another
      // app's audio into a recording puts it in the take.
      interruptionMode: 'doNotMix',
      shouldPlayInBackground: false,
      shouldRouteThroughEarpiece: false,
    })
    borrowed = true
  } catch {
    // A platform that will not change its mode still gets to try the recorder.
  }
}

/**
 * Give the session back to playback.
 *
 * The important half of the pair, and the one that was missing. Called wherever a
 * recording ends — kept, discarded, or abandoned by unmounting the card — so the
 * category returns to `.playback` and the next thing the creator hears comes out
 * of the speaker at the volume they expect.
 */
export async function endRecordingSession(): Promise<void> {
  if (Platform.OS === 'web' || !borrowed) return
  try {
    const {setAudioModeAsync} = await import('expo-audio')
    await setAudioModeAsync({...PLAYBACK, shouldPlayInBackground: false})
  } catch {
    // Nothing to do: the recorder's session is a default, not a promise we made.
  } finally {
    borrowed = false
  }
}

/** Testing seam — the module remembers whether it owes the session back. */
export function isRecordingSessionBorrowed(): boolean {
  return borrowed
}
